/**
 * Renderer-side wrapper around the Database Blueprint IPC channels.
 * Centralises the fire-and-forget analytics + the error toast pattern so
 * dialogs / context-menu handlers stay focused on UI state.
 */

import type {
  ApplyBlueprintTableEditsRequest,
  ApplyBlueprintTableEditsResult,
  BlueprintRegistryEntry,
  BundleWriteProgress,
  BlueprintDataTableRef,
  BlueprintDataTable,
  BlueprintExcludedObject,
  BlueprintGitStatus,
  BlueprintMetadata,
  BlueprintObjectTypes,
  BlueprintSetupProgressEvent,
  PreviewBundle,
  PreviewBundleId,
  ReadBlueprintMetadataRequest,
  ReadBlueprintMetadataResult,
  ReadSidecarResult,
  RebuildBlueprintDataCacheRequest,
  RebuildBlueprintDataCacheResult,
  SetupBlueprintRequest,
  SetupBlueprintResult,
  ThreeWayMergeProgressEvent,
  ThreeWayMergeResult,
  UserFacingEngineId,
  WriteBlueprintDataResult,
} from '../types/databaseBlueprintTypes';
import type { ProRequiredError } from '@/core/licensing/types';

function api() {
  const electronApi = window.electronAPI;
  if (!electronApi || !electronApi.databaseBlueprint) {
    throw new Error('Database Blueprint IPC is unavailable (window.electronAPI.databaseBlueprint missing).');
  }
  return electronApi.databaseBlueprint;
}

export async function setupBlueprint(request: SetupBlueprintRequest): Promise<SetupBlueprintResult> {
  return api().setup(request);
}

/**
 * Streaming variant of `setupBlueprint` that emits progress events the caller
 * can render in a sub-dialog (spinner + log + progress bar). The returned
 * promise resolves to the same `SetupBlueprintResult` as `setupBlueprint` —
 * `{ success: false, aborted: true, error }` indicates the user-requested
 * abort completed cleanly (no partial files left behind).
 */
export async function setupBlueprintWithProgress(
  request: SetupBlueprintRequest & { operationId: string },
  onProgress: (event: BlueprintSetupProgressEvent) => void,
): Promise<SetupBlueprintResult> {
  const unsubscribe = api().onSetupProgress((evt) => {
    if (evt.operationId === request.operationId) onProgress(evt);
  });
  try {
    return await api().setupWithProgress(request);
  } finally {
    unsubscribe();
  }
}

/**
 * Ask the main process to abort an in-flight setup operation. Resolves once
 * the abort request was delivered — the actual cleanup happens inside the
 * setup handler, which will emit a final `aborted` progress event and resolve
 * the original `setupBlueprintWithProgress` promise with `{ aborted: true }`.
 */
export async function abortBlueprintSetup(
  operationId: string,
): Promise<{ aborted: boolean }> {
  return api().abortSetup({ operationId });
}

export async function readBlueprintMetadata(
  request: ReadBlueprintMetadataRequest
): Promise<ReadBlueprintMetadataResult> {
  return api().readMetadata(request);
}

export async function pickBlueprintFolder(
  defaultPath?: string
): Promise<{ directoryPath?: string; cancelled?: boolean; error?: string }> {
  return api().selectFolder(defaultPath ? { defaultPath } : undefined);
}

export async function folderIsEmpty(
  folderPath: string
): Promise<{ empty: boolean; exists: boolean; error?: string }> {
  return api().folderIsEmpty({ folderPath });
}

/**
 * List the main-process blueprint registry. Returns [] on any failure — the
 * caller (hydrate-on-init) must never throw, since a missing/corrupt registry
 * should degrade to the persisted renderer state, not crash startup.
 */
export async function listBlueprintRegistry(): Promise<BlueprintRegistryEntry[]> {
  try {
    const result = await api().registryList();
    return result.success ? result.entries : [];
  } catch {
    return [];
  }
}

/** Write-through: register a blueprint folder. Fire-and-forget (swallows errors). */
export async function addBlueprintToRegistry(input: { folderPath: string; label: string }): Promise<void> {
  try {
    await api().registryAdd(input);
  } catch {
    /* fire-and-forget — projection is best-effort */
  }
}

/** Write-through: drop a blueprint folder from the registry. Fire-and-forget. */
export async function removeBlueprintFromRegistry(folderPath: string): Promise<void> {
  try {
    await api().registryRemove({ folderPath });
  } catch {
    /* fire-and-forget */
  }
}

/**
 * Phase-2 unified Refresh-from-DB flow — stage the entire emit (DDL + data
 * sidecars + snapshot) into a server-cached `PreviewBundle`. Resolves with
 * a single Promise; no progress events fire during the build itself. Pair
 * with `writeBundle` to commit or `discardBundle` to abort.
 */
export async function buildBundle(args: {
  folderPath: string;
  linkLabel: string;
  blueprintDataTables: ReadonlyArray<BlueprintDataTable>;
}): Promise<
  | { success: true; bundle: PreviewBundle }
  | { success: false; error: string }
  | ProRequiredError
> {
  return api().buildBundle({
    folderPath: args.folderPath,
    linkLabel: args.linkLabel,
    blueprintDataTables: args.blueprintDataTables.slice(),
  });
}

/**
 * Commit a cached `PreviewBundle` to disk. When `operationId` and
 * `onProgress` are both supplied, subscribes to the bundle-write progress
 * channel and filters events by `operationId` so the renderer can render
 * "Writing dbo.Customers.sql (5/234)" without seeing other in-flight writes'
 * events.
 */
export async function writeBundle(args: {
  bundleId: PreviewBundleId;
  folderPath: string;
  blueprintDataTables: ReadonlyArray<BlueprintDataTable>;
  operationId?: string;
  onProgress?: (event: BundleWriteProgress) => void;
}): Promise<
  | { success: true; written: number; metadata: BlueprintMetadata }
  | { success: false; error: string }
> {
  let unsubscribe: (() => void) | undefined;
  if (args.onProgress && args.operationId) {
    const opId = args.operationId;
    const handler = args.onProgress;
    unsubscribe = api().onBundleWriteProgress((evt) => {
      if ((evt as { operationId?: string }).operationId === opId) {
        handler(evt);
      }
    });
  }
  try {
    return await api().writeBundle({
      bundleId: args.bundleId,
      folderPath: args.folderPath,
      blueprintDataTables: args.blueprintDataTables.slice(),
      operationId: args.operationId,
    });
  } finally {
    unsubscribe?.();
  }
}

/**
 * Drop a cached `PreviewBundle` without writing. Best-effort — resolves
 * with `true` if the bundle was found and dropped, `false` if it had
 * already expired or never existed.
 */
export async function discardBundle(bundleId: PreviewBundleId): Promise<boolean> {
  const result = await api().discardBundle({ bundleId });
  return result.discarded;
}

export async function threeWayMergeBlueprint(
  folderPath: string,
  linkLabel: string,
  /**
   * Optional correlation id so the caller can match live progress events
   * (see `onThreeWayMergeProgress`) to this specific in-flight merge.
   */
  requestId?: string,
): Promise<ThreeWayMergeResult> {
  return api().threeWayMerge({ folderPath, linkLabel, requestId });
}

/**
 * Subscribe to live progress events emitted while a three-way merge computes.
 * Returns an unsubscribe function. The caller is responsible for filtering by
 * `requestId` to ignore events from a previous/concurrent merge.
 */
export function onThreeWayMergeProgress(
  callback: (event: ThreeWayMergeProgressEvent) => void,
): () => void {
  return api().onThreeWayMergeProgress(callback);
}

/**
 * H4 — persist a single baseline file. Called by the three-way merge
 * "Take local" / "Take DB" resolve handlers so the chosen content becomes
 * the new baseline (otherwise the resolution is in-memory only and the
 * conflict reappears on the next `reloadMerge()` or dialog re-open).
 */
export async function writeBlueprintBaselineFile(args: {
  folderPath: string;
  linkLabel: string;
  relPath: string;
  content: string;
}): Promise<{ success: true } | { success: false; error: string }> {
  return api().writeBaselineFile(args);
}

export async function addBlueprintLink(request: {
  folderPath: string;
  label: string;
  connectionId: string;
  connectionDisplayName: string;
  database: string;
  engine: UserFacingEngineId;
}): Promise<
  | { success: true; metadata: BlueprintMetadata }
  | { success: false; error: string }
> {
  return api().addLink(request);
}

export async function removeBlueprintLink(
  folderPath: string,
  label: string,
): Promise<
  | { success: true; metadata: BlueprintMetadata }
  | { success: false; error: string }
> {
  return api().removeLink({ folderPath, label });
}

/**
 * Persist which linked DB is currently "active" for the given blueprint folder.
 * Backed by `database-blueprint-set-active-link`. The IPC writes the choice to
 * `jamsql-blueprint.json` so it survives reloads.
 */
export async function setActiveLink(args: {
  folderPath: string;
  label: string;
}): Promise<
  | { success: true; metadata: BlueprintMetadata }
  | { success: false; error: string }
> {
  return api().setActiveLink(args);
}

export async function getBlueprintGitStatus(
  folderPath: string,
): Promise<
  | { success: true; isRepo: boolean; status: BlueprintGitStatus | null }
  | { success: false; error: string }
> {
  return api().gitStatus({ folderPath });
}

export async function initBlueprintGitRepo(
  folderPath: string,
): Promise<{ success: boolean; error?: string }> {
  return api().gitInit({ folderPath });
}

export async function commitBlueprintChanges(
  folderPath: string,
  options?: { message?: string; linkLabel?: string },
): Promise<
  | { success: true; commit: string; message: string }
  | { success: false; error: string }
> {
  return api().gitCommit({
    folderPath,
    message: options?.message,
    linkLabel: options?.linkLabel,
  });
}

export async function listBlueprintGitBranches(
  folderPath: string,
): Promise<{ success: true; branches: string[] } | { success: false; error: string }> {
  return api().gitBranches({ folderPath });
}

export async function checkoutBlueprintGitBranch(
  folderPath: string,
  branchName: string,
): Promise<{ success: true } | { success: false; error: string }> {
  return api().gitCheckout({ folderPath, branchName });
}

export async function pullBlueprintGit(
  folderPath: string,
): Promise<{ success: true; summary: string } | { success: false; error: string }> {
  return api().gitPull({ folderPath });
}

export async function pushBlueprintGit(
  folderPath: string,
): Promise<{ success: true; summary: string } | { success: false; error: string }> {
  return api().gitPush({ folderPath });
}

export async function markBlueprintConflictResolved(
  folderPath: string,
  filePath: string,
): Promise<{ success: true } | { success: false; error: string }> {
  return api().gitMarkResolved({ folderPath, filePath });
}

export async function setBlueprintExclusions(
  folderPath: string,
  exclusions: ReadonlyArray<BlueprintExcludedObject>,
): Promise<
  | { success: true; metadata: BlueprintMetadata }
  | { success: false; error: string }
> {
  return api().setExclusions({
    folderPath,
    exclusions: exclusions.slice(),
  });
}

export async function listBlueprintObjects(
  folderPath: string,
  linkLabel: string,
): Promise<
  | {
      success: true;
      objects: Array<{
        kind: 'table' | 'view' | 'procedure' | 'function';
        schema: string;
        name: string;
      }>;
    }
  | { success: false; error: string }
> {
  return api().listBlueprintObjects({ folderPath, linkLabel });
}

/**
 * Probe the blueprint folder for missing per-table `data-cache/*.json` sidecars
 * under `.jamsql/` (needed by Sync Data folder→DB). Drives the top-of-tab
 * BlueprintSidecarBanner so users get a one-click Refresh recovery before they
 * trigger a workflow that depends on those sidecars.
 *
 * I2b (drop-snapshot plan): the structured schema snapshot is gone — the
 * folder's `.sql` files are the complete source of truth — so this probe only
 * covers the data-cache half.
 */
export async function detectMissingBlueprintSidecars(
  folderPath: string,
): Promise<
  | {
      success: true;
      missingDataCaches: Array<{ schema: string; name: string }>;
    }
  | { success: false; error: string }
> {
  return api().detectMissingSidecars({ folderPath });
}

/**
 * List the tables for which a `.data.sql` file exists in the given blueprint
 * folder. Seed for the Slice-10 Sync Data dialog's table picker; degrades
 * to an empty list when the folder doesn't exist or has no `.data.sql`
 * files yet.
 */
export async function listBlueprintDataTables(
  folderPath: string,
): Promise<
  | { success: true; tables: BlueprintDataTableRef[] }
  | { success: false; error: string }
> {
  return api().listBlueprintDataTables({ folderPath });
}

/**
 * Return the requested blueprint-dataed tables in parent-first
 * topological order based on FOREIGN KEY constraints captured in the
 * blueprint folder's `.jamsql/snapshot.json` sidecar. The Sync Data dialog
 * uses this to render its "FK-ordered run plan" disclosure AND to drive
 * the actual run order so child INSERTs land after their parents'.
 * Falls back to the input order when the sidecar is absent or
 * unparseable.
 */
export async function fkOrderBlueprintDataTables(args: {
  folderPath: string;
  tables: ReadonlyArray<{ schema: string; table: string }>;
  linkLabel: string;
}): Promise<
  | { success: true; orderedTables: Array<{ schema: string; table: string }> }
  | { success: false; error: string }
> {
  return api().fkOrderDataTables(args);
}

/**
 * WS-D step 5 — offline data-cache rebuild. Reconstructs `Data/*.data.json`
 * sidecars from their sibling `.data.sql` upsert scripts alone (no live DB
 * round-trip). Drives the BlueprintSidecarBanner's "Rebuild from .data.sql"
 * action, the offline alternative to Refresh-from-DB.
 */
export async function rebuildBlueprintDataCache(
  request: RebuildBlueprintDataCacheRequest,
): Promise<RebuildBlueprintDataCacheResult> {
  return api().rebuildDataCache(request);
}

/**
 * Read a `.data.sql` sidecar's PK + column list without loading the
 * row payload. Used by the Sync Data progress orchestrator (Slice 10c)
 * to construct `keyMappings` and `valueMappings` per table before
 * calling `dataCompare.start`.
 */
export async function readSidecarPk(args: {
  folderPath: string;
  schema: string;
  table: string;
}): Promise<
  | {
      success: true;
      pk: string[];
      /**
       * T8 — `isIdentity: true` for MSSQL IDENTITY columns so the Sync Data
       * dialog can thread the names into the generated INSERT script's
       * `SET IDENTITY_INSERT ON/OFF` wrapper (W5 Sub-bug B). Absent on legacy
       * sidecars and on engines whose metadata layer doesn't surface it.
       *
       * W5 — `isWritable: false` for generated / computed columns. The Sync
       * Data dialog drops these from `valueMappings` so the emitted
       * INSERT/UPDATE never targets them. Absence is read as "writable" so
       * legacy sidecars and normal columns Just Work.
       */
      columns: Array<{
        name: string;
        type: string;
        isIdentity?: boolean;
        isWritable?: boolean;
      }>;
    }
  | { success: false; error: string }
> {
  return api().readSidecarPk(args);
}

/**
 * Read the full `.data.json` sidecar (columns + rows + pk + capturedAt) for
 * a blueprint-dataed table. Returns `{success: true, sidecar: null}` when the
 * file is missing on disk so the viewer can render an empty state.
 */
export async function readSidecar(args: {
  folderPath: string;
  schema: string;
  name: string;
}): Promise<ReadSidecarResult> {
  return api().readSidecar(args);
}

/**
 * Write an EDITED table rowset back to the blueprint folder (Table Explorer
 * Blueprint-edit mode Save). Regenerates the `.data.sql` upsert + `.data.json`
 * sidecar from the supplied rows + column metadata — never queries a database.
 */
export async function applyBlueprintTableEdits(
  request: ApplyBlueprintTableEditsRequest,
): Promise<ApplyBlueprintTableEditsResult> {
  return api().applyTableEdits(request);
}

/**
 * Apply a no-PK table's `.data.sql` sidecar via DELETE + re-INSERT (Slice 9).
 * Used by the Sync Data progress orchestrator when `allowDestructiveReplay: true`.
 */
export async function applyDestructiveReplay(args: {
  folderPath: string;
  schema: string;
  table: string;
  connectionId: string;
  database: string;
  engine: import('../types/databaseBlueprintTypes').UserFacingEngineId;
}): Promise<{ success: true; statements: number } | { success: false; error: string }> {
  return api().applyDestructiveReplay(args);
}

export async function listSourceObjects(args: {
  connectionId: string;
  database: string;
  engine: string;
}): Promise<
  | {
      success: true;
      objects: Array<{
        kind: 'table' | 'view' | 'procedure' | 'function';
        schema: string;
        name: string;
      }>;
    }
  | { success: false; error: string }
> {
  return api().listSourceObjects(args);
}

export async function countTableRows(args: {
  connectionId: string;
  database: string;
  engine: UserFacingEngineId;
  tables: ReadonlyArray<{ schema: string; name: string }>;
}): Promise<
  | {
      success: true;
      counts: Array<{
        schema: string;
        name: string;
        rows: number | null;
        hasPk: boolean;
        error?: string;
      }>;
    }
  | { success: false; error: string }
> {
  return api().countRows({
    connectionId: args.connectionId,
    database: args.database,
    engine: args.engine,
    tables: args.tables.map((t) => ({ schema: t.schema, name: t.name })),
  });
}

export async function writeBlueprintData(args: {
  folderPath: string;
  connectionId: string;
  database: string;
  engine: UserFacingEngineId;
  tables: ReadonlyArray<BlueprintDataTable>;
}): Promise<WriteBlueprintDataResult> {
  return api().writeBlueprintData({
    folderPath: args.folderPath,
    connectionId: args.connectionId,
    database: args.database,
    engine: args.engine,
    tables: args.tables.map((t) =>
      t.allowDestructiveReplay === true
        ? { schema: t.schema, name: t.name, allowDestructiveReplay: true }
        : { schema: t.schema, name: t.name },
    ),
  });
}

/**
 * Patch the on-disk `jamsql-blueprint.json` (and only that file — no DDL is
 * re-emitted). Used by the toolbar's Settings button (Setup dialog in edit
 * mode). Each field is optional — only the keys passed are applied.
 */
export async function updateBlueprintSettings(request: {
  folderPath: string;
  label?: string;
  includedObjectTypes?: BlueprintObjectTypes;
  includedSchemas?: string[];
  excludedObjects?: ReadonlyArray<BlueprintExcludedObject>;
  blueprintDataTables?: ReadonlyArray<BlueprintDataTable>;
}): Promise<
  | { success: true; metadata: BlueprintMetadata }
  | { success: false; error: string }
> {
  return api().updateSettings({
    folderPath: request.folderPath,
    label: request.label,
    includedObjectTypes: request.includedObjectTypes,
    includedSchemas: request.includedSchemas,
    excludedObjects: request.excludedObjects ? request.excludedObjects.slice() : undefined,
    blueprintDataTables: request.blueprintDataTables ? request.blueprintDataTables.slice() : undefined,
  });
}

/**
 * Stat a single file on disk through the Database Blueprint IPC. Returns the
 * file's `mtimeMs` so callers (currently `BlueprintPeekViewer`) can detect
 * external edits and auto-reload their displayed content.
 *
 * Bug F11 — `folderPath` is required so the main process can enforce that
 * `filePath` is contained inside the blueprint folder (defense-in-depth: a
 * compromised renderer must not be able to probe arbitrary filesystem
 * mtimes through this channel).
 */
export async function statBlueprintFile(params: {
  folderPath: string;
  filePath: string;
}): Promise<
  | { success: true; mtimeMs: number }
  | { success: false; error: string }
> {
  return api().statFile({
    folderPath: params.folderPath,
    filePath: params.filePath,
  });
}

export async function listUserSchemas(
  connectionId: string,
  database: string,
  engine: UserFacingEngineId,
  includeSystemSchemas = false
): Promise<string[]> {
  const result = await api().listSchemas({
    connectionId,
    database,
    engine,
    includeSystemSchemas,
  });
  if (!result.success) {
    throw new Error(result.error);
  }
  return result.schemas;
}
